import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  loadShareExpiryChoice,
  type ShareExpiryChoice,
  type ShareExpiryMode,
} from '../lib/shareExpiry'
import { userMessageForError } from '../lib/userError'
import { useI18n } from '../i18n/useI18n'

const OPTIONS: ShareExpiryMode[] = ['DAY_1', 'DAYS_7', 'DAYS_30', 'RETENTION', 'CUSTOM']

function tomorrowDateValue(): string {
  const date = new Date()
  date.setDate(date.getDate() + 1)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function ShareExpiryDialog({
  targetType,
  targetTitle,
  action,
  currentExpiry,
  shareDownloadLimit,
  onCancel,
  onConfirm,
}: {
  targetType: 'song' | 'album'
  targetTitle: string
  action: 'start' | 'change'
  currentExpiry?: string | null
  shareDownloadLimit?: number | null
  onCancel: () => void
  onConfirm: (choice: ShareExpiryChoice) => Promise<void>
}) {
  const { locale, t } = useI18n()
  const initial = loadShareExpiryChoice()
  const [mode, setMode] = useState<ShareExpiryMode>(initial.mode)
  const [customDate, setCustomDate] = useState(initial.customDate || tomorrowDateValue())
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setError('')
  }, [mode, customDate])

  async function submit() {
    setSubmitting(true)
    setError('')
    try {
      await onConfirm(mode === 'CUSTOM' ? { mode, customDate } : { mode })
    } catch (cause) {
      setError(userMessageForError(cause, t('shareExpiry.error.save')))
      setSubmitting(false)
    }
  }

  return createPortal(
    <div className="share-expiry-layer" role="presentation">
      <button
        type="button"
        className="share-expiry-backdrop"
        aria-label={t('shareExpiry.closeAria')}
        onClick={submitting ? undefined : onCancel}
      />
      <section
        className="card share-expiry-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-expiry-title"
      >
        <div className="share-expiry-header">
          <div>
            <p className="eyebrow">{t(targetType === 'song' ? 'shareExpiry.eyebrow.song' : 'shareExpiry.eyebrow.album')}</p>
            <h3 id="share-expiry-title">
              {action === 'start' ? t('shareExpiry.title.start') : t('shareExpiry.title.change')}
            </h3>
            <p className="muted share-expiry-target">「{targetTitle}」</p>
          </div>
          <button
            type="button"
            className="share-expiry-close"
            disabled={submitting}
            onClick={onCancel}
            aria-label={t('shareExpiry.closeAria')}
            title={t('common.close')}
          >
            ×
          </button>
        </div>

        {currentExpiry && action === 'change' && (
          <p className="muted share-expiry-current">
            {t('shareExpiry.currentExpiry', { date: new Date(currentExpiry).toLocaleDateString(locale) })}
          </p>
        )}

        <div className="share-expiry-options">
          {OPTIONS.map((option) => (
            <label
              className={mode === option ? 'share-expiry-option is-active' : 'share-expiry-option'}
              key={option}
            >
              <input
                type="radio"
                name="share-expiry-mode"
                value={option}
                checked={mode === option}
                disabled={submitting}
                onChange={() => setMode(option)}
              />
              <span>{t(`shareExpiry.option.${option}`)}</span>
            </label>
          ))}
        </div>

        {mode === 'CUSTOM' && (
          <label className="share-expiry-custom">
            <span>{t('shareExpiry.customEndDate')}</span>
            <input
              type="date"
              value={customDate}
              min={tomorrowDateValue()}
              disabled={submitting}
              onChange={(event) => setCustomDate(event.target.value)}
            />
          </label>
        )}

        <p className="muted share-expiry-help">
          {t('shareExpiry.help')}
        </p>

        {targetType === 'song' && action === 'start' && shareDownloadLimit != null && (
          <p className="notice warning share-download-limit-notice">
            {t('shareExpiry.downloadLimitNotice', { limit: shareDownloadLimit })}
          </p>
        )}

        {error && <p className="notice error" role="alert">{error}</p>}

        <div className="control-row share-expiry-actions">
          <button type="button" className="button ghost" disabled={submitting} onClick={onCancel}>
            {t('common.cancel')}
          </button>
          <button type="button" className="button primary" disabled={submitting} onClick={() => void submit()}>
            {submitting ? t('common.saving') : action === 'start' ? t('vault.action.startShare') : t('shareExpiry.action.change')}
          </button>
        </div>
      </section>
    </div>,
    document.body,
  )
}
